import type { OnInit } from '@angular/core';
import {
  Component,
  computed,
  effect,
  forwardRef,
  inject,
  Injector,
  input,
  linkedSignal,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import type { OnyxOptional } from '@onyx/angular';
import {
  ActionHelper,
  OnyxAddressComponent,
  OnyxDatePipe,
  OnyxIconComponent,
  OnyxLinkComponent,
  OnyxTimePipe,
} from '@onyx/angular';
import { partition } from 'lodash';
import { DelegationsRoute } from '../../../dashboard/delegations/common/enums/delegations-route';
import { DelegationHelper } from '../../../dashboard/delegations/common/helpers/delegation.helper';
import { DelegationActionModalMode } from '../../../dashboard/delegations/delegation-action-modal/delegation-action-modal.component';
import { TechnicalDelegationPointName } from '../../../dashboard/fleet/common/enums/technical-delegation-point-name';
import { DelegationPointType } from '../../../dashboard/fleet/common/enums/technical-point-type';
import { MapRouteFilter } from '../../../dashboard/map/common/enums/map-route-filter';
import { OrderPointStatus } from '../../../dashboard/orders/common/enums/order-point-status';
import type { SimplifiedOrder } from '../../../dashboard/orders/common/interfaces/order';
import type { FuelType } from '../../enums/fuel-type';
import { RouteHelper } from '../../helpers/route.helper';
import { RoutePointValueHelper } from '../../helpers/route-point-value.helper';
import type { TelematicsItem } from '../../interfaces/telematics/telematics-item';
import type { RoutePoint, RoutePointKey } from '../../services/route.service';
import { RouteService } from '../../services/route.service';
import { RoutePointMarkerComponent } from './route-point/route-point-marker/route-point-marker.component';
import { RoutePointComponent } from './route-point/route-point.component';

@Component({
  selector: 'app-route',
  imports: [
    forwardRef(() => RoutePointComponent),
    OnyxIconComponent,
    TranslatePipe,
    OnyxLinkComponent,
    RoutePointMarkerComponent,
    OnyxAddressComponent,
    OnyxTimePipe,
    OnyxDatePipe,
  ],
  templateUrl: './route.component.html',
  styleUrl: './route.component.scss',
})
export class RouteComponent implements OnInit {
  private readonly routeService = inject(RouteService);
  private readonly injector = inject(Injector);
  private readonly delegationHelper = inject(DelegationHelper);

  public readonly points = input.required<RoutePoint[]>();
  public readonly telematicsItem = input<TelematicsItem>();
  public readonly vehicleUuid = input<OnyxOptional<string>>();
  public readonly fuelType = input.required<OnyxOptional<FuelType>>();
  public readonly order = input<OnyxOptional<SimplifiedOrder>>(null);
  public readonly hideDelegationCycle = input(false);

  protected readonly I18N = 'route';

  protected readonly RouteHelper = RouteHelper;

  protected routePoints = linkedSignal({
    source: () => this.points(),
    computation: (routePoints) => {
      const [endCycleDelegationPoints, points] = partition(
        routePoints,
        (point) => {
          const value = RoutePointValueHelper.getValue(point);
          if (!RouteHelper.isDelegationPoint(value, DelegationPointType.END)) {
            return false;
          }

          const delegation = value.delegationData.delegation.delegation;
          return (
            (value.delegationData.hasCycleActivePoints ||
              DelegationHelper.isActive(delegation)) &&
            !DelegationHelper.isReturning(delegation)
          );
        },
      );

      return { endCycleDelegationPoints, points };
    },
  });
  protected isOrderFilter = computed(
    () =>
      !Object.values(MapRouteFilter).includes(
        this.routeService.filter() as MapRouteFilter,
      ),
  );
  protected showDelegationCycle = computed(
    () => !this.hideDelegationCycle() && !this.isOrderFilter(),
  );
  protected lastCyclePoints = computed(() => {
    const points = this.points();
    const telematicsItem = this.telematicsItem();
    if (!telematicsItem) {
      return RouteHelper.getLastDelegationCyclePoints(points);
    }

    return RouteHelper.getLastDelegationCyclePoints(
      [MapRouteFilter.WHOLE_HISTORY].includes(
        this.routeService.filter() as MapRouteFilter,
      )
        ? points
        : (telematicsItem.routePoints ?? []),
    );
  });
  protected currentEndingDelegations = computed(() => {
    const lastCyclePoints = this.lastCyclePoints().filter(
      (point) => !RouteHelper.isDelegationPoint(point, DelegationPointType.END),
    );
    const firstPoint = lastCyclePoints.at(0);
    const lastPoint = lastCyclePoints.at(-1);
    if (!firstPoint || !lastPoint) return [];

    const lastPointDelegationUuids = RouteHelper.getDelegationUuids(lastPoint);
    if (!lastPointDelegationUuids.length) return [];

    const delegationData = RouteHelper.getDelegationData(firstPoint);
    if (!delegationData) return [];

    return delegationData.delegations.filter(({ uuid }) =>
      lastPointDelegationUuids.includes(uuid),
    );
  });
  protected incompleteEndCycleDelegationPoints = computed(() => {
    const endingDelegations = this.currentEndingDelegations();
    if (!endingDelegations.length) return [];

    const lastPoint = this.lastCyclePoints().at(-1);
    if (!lastPoint) return [];

    const status = RouteHelper.getStatus(lastPoint);
    if (!status) return [];

    if (
      RouteHelper.isDelegationPoint(lastPoint, DelegationPointType.END) &&
      [OrderPointStatus.COMPLETED, OrderPointStatus.CANCELED].includes(status)
    ) {
      return [];
    }

    const existingEndUuids = new Set(
      this.routePoints()
        .endCycleDelegationPoints.map(
          (point) =>
            RouteHelper.getDelegationData(point)?.delegation.delegation.uuid,
        )
        .filter((uuid) => !!uuid),
    );

    return endingDelegations.filter(
      (delegation) =>
        !existingEndUuids.has(delegation.uuid) &&
        !DelegationHelper.isReturning(delegation),
    );
  });
  protected hasActiveCycle = computed(() =>
    RouteHelper.hasActiveCycle(this.lastCyclePoints()),
  );

  public ngOnInit(): void {
    effect(
      () => {
        const uuid = this.vehicleUuid() ?? this.telematicsItem()?.uuid;
        const fuelType = this.fuelType();
        const vehicle = uuid || fuelType ? { uuid, fuelType } : null;

        this.routeService.points.set(this.routePoints().points);
        this.routeService.vehicle.set(vehicle);
        this.routeService.telematicsItem.set(this.telematicsItem());
        this.routeService.order.update((order) => this.order() ?? order);
      },
      { injector: this.injector },
    );
  }

  protected trackPoint(point: RoutePoint): RoutePointKey | null {
    return this.routeService.getKey(point);
  }

  protected isVisible(
    point: RoutePoint,
    isFirst: boolean,
    isLast: boolean,
  ): boolean {
    if (isFirst || isLast) return true;
    return RouteHelper.isIncludedInOrder(point);
  }

  protected editDelegation(uuid: string): void {
    ActionHelper.openRoute(
      DelegationsRoute.EDIT_DELEGATION.replace(':uuid', uuid),
    );
  }

  protected openActionModal(): void {
    const endingDelegations = this.currentEndingDelegations();
    const telematicsItem = this.telematicsItem();
    if (!endingDelegations.length || !telematicsItem) return;

    this.delegationHelper.openActionModal(
      DelegationActionModalMode.RETURN,
      endingDelegations,
      telematicsItem,
    );
  }

  protected getDelegationLabel(index: number): TechnicalDelegationPointName {
    const missingCount = this.incompleteEndCycleDelegationPoints().length;
    const totalCount =
      this.routePoints().endCycleDelegationPoints.length + missingCount;

    if (totalCount <= 1 || index === missingCount - 1) {
      return TechnicalDelegationPointName.END;
    }

    return TechnicalDelegationPointName.DELEGATION_END;
  }
}
